import { Link } from "react-router-dom";

interface Course {
  tag: string;
  name: string;
  price: string;
  duration: string;
  forWho: string;
  features: string[];
  icon: string;
  featured: boolean;
  description: string;
  badge?: string;
}

const courses: Course[] = [
  {
    tag: "CERTIFICATION",
    name: "BLS Provider Class",
    price: "$85",
    duration: "~4 hours",
    forWho: "Healthcare professionals, students",
    features: [
      "CPR for adults, children & infants",
      "AED operation & team dynamics",
      "Airway management techniques",
      "Same-day AHA certification card",
    ],
    icon: "ri-heart-pulse-line",
    featured: true,
    description: "The gold standard in Basic Life Support. Led by Registered Nurses with real clinical insight — prepares you to respond confidently in any cardiac emergency.",
  },
  {
    tag: "RENEWAL",
    name: "BLS Renewal",
    price: "$75",
    duration: "~2–3 hours",
    forWho: "Current BLS certificate holders",
    features: [
      "Updated AHA algorithms",
      "Skills testing & competency check",
      "New certification card issued",
      "Streamlined renewal process",
    ],
    icon: "ri-refresh-line",
    featured: false,
    description: "Keep your certification current with our efficient renewal course. Covers the latest AHA updates and keeps your skills sharp and verified.",
  },
  {
    tag: "BLENDED",
    name: "BLS Blended (Online + Hands-On)",
    price: "$95",
    duration: "Flexible",
    forWho: "Busy professionals, self-paced learners",
    features: [
      "Complete online portion at your convenience",
      "In-person skills check session",
      "Same-day certification at skills session",
      "Best for flexible schedules",
    ],
    icon: "ri-computer-line",
    featured: false,
    description: "Complete the online learning module at your own pace, then come in for a hands-on skills evaluation. Perfect for busy schedules.",
    badge: "Flexible",
  },
  {
    tag: "SKILLS CHECK",
    name: "BLS Heartcode Skills Session",
    price: "$55",
    duration: "~1–2 hours",
    forWho: "Heartcode online completers",
    features: [
      "For students who completed Heartcode online",
      "Hands-on evaluation only",
      "Fast & efficient certification",
      "AHA-aligned credential",
    ],
    icon: "ri-qr-scan-2-line",
    featured: false,
    description: "Already completed the Heartcode online portion? Come in for your hands-on skills evaluation and walk out certified the same day.",
  },
  {
    tag: "HEARTSAVER",
    name: "Heartsaver CPR / AED / First Aid",
    price: "$105",
    duration: "~3–4 hours",
    forWho: "General public, businesses, schools",
    features: [
      "Full CPR, AED & First Aid coverage",
      "Adult, child & infant CPR",
      "Hands-on AED practice",
      "Same-day certification",
    ],
    icon: "ri-first-aid-kit-line",
    featured: true,
    description: "Comprehensive training covering CPR, AED usage, and first aid. Ideal for workplaces, schools, and anyone who wants to be prepared for emergencies.",
  },
  {
    tag: "ADVANCED",
    name: "ACLS Provider (Initial)",
    price: "$230",
    duration: "1–2 days",
    forWho: "Physicians, nurses, paramedics",
    features: [
      "Advanced airway management",
      "Rhythm recognition & pharmacology",
      "Megacode simulations",
      "Real-life emergency scenarios",
      "Same-day certification",
    ],
    icon: "ri-heart-3-line",
    featured: true,
    description: "Advanced Cardiovascular Life Support for experienced providers. Master the algorithms, pharmacology, and team leadership needed in true cardiac emergencies.",
    badge: "Professional",
  },
  {
    tag: "RENEWAL",
    name: "ACLS Renewal / Refresher",
    price: "$175",
    duration: "~4–6 hours",
    forWho: "Current ACLS certificate holders",
    features: [
      "Updated ACLS protocols",
      "Hands-on skills practice",
      "Efficient recertification process",
      "AHA-aligned credential",
    ],
    icon: "ri-refresh-line",
    featured: false,
    description: "Keep your ACLS current with updated protocols, hands-on practice, and efficient recertification — all RN-led.",
  },
  {
    tag: "PEDIATRIC",
    name: "PALS Provider (Initial)",
    price: "$230",
    duration: "1–2 days",
    forWho: "Pediatric healthcare providers",
    features: [
      "Pediatric assessment & intervention",
      "Respiratory and cardiac scenarios",
      "Hands-on simulations",
      "Same-day certification",
    ],
    icon: "ri-baby-line",
    featured: true,
    description: "Pediatric Advanced Life Support focused on critical emergencies in infants and children. RN-led with real-world simulation scenarios.",
    badge: "Pediatric",
  },
  {
    tag: "RENEWAL",
    name: "PALS Renewal / Refresher",
    price: "$175",
    duration: "~4–6 hours",
    forWho: "Current PALS certificate holders",
    features: [
      "Updated pediatric emergency protocols",
      "Focused skills competency review",
      "Efficient recertification",
      "AHA-aligned credential",
    ],
    icon: "ri-refresh-line",
    featured: false,
    description: "Stay current on pediatric emergency protocols with this streamlined renewal. Designed to be efficient and thorough.",
  },
  {
    tag: "BUNDLE",
    name: "ACLS + BLS Combo",
    price: "$250",
    duration: "1–2 days",
    forWho: "Healthcare professionals",
    features: [
      "Dual certification in one visit",
      "Streamlined scheduling",
      "Save vs. individual pricing",
      "Same-day certification",
    ],
    icon: "ri-stack-line",
    featured: false,
    description: "Efficient dual certification for busy healthcare professionals. Get both ACLS and BLS certified in one streamlined visit.",
    badge: "Bundle",
  },
  {
    tag: "BEST VALUE",
    name: "ACLS + PALS + BLS Bundle",
    price: "$425",
    duration: "1–2 days",
    forWho: "All healthcare professionals",
    features: [
      "All three certifications included",
      "Hands-on RN-led training",
      "Same-day certification",
      "Streamlined scheduling for efficiency",
    ],
    icon: "ri-trophy-line",
    featured: true,
    description: "The ultimate certification package. Get ACLS, PALS, and BLS in one streamlined experience with maximum savings and minimum time away from work.",
    badge: "Best Value",
  },
  {
    tag: "INSTRUCTOR",
    name: "BLS Instructor Course",
    price: "$500",
    duration: "1 day",
    forWho: "Aspiring AHA instructors",
    features: [
      "Step-by-step certification guidance",
      "Teaching methodology & course structure",
      "Hands-on teaching practice",
      "AHA-aligned material guidance",
      "Ongoing mentorship program included",
    ],
    icon: "ri-user-star-line",
    featured: false,
    description: "Become a certified CPR instructor with full mentorship support. Includes guidance on building your own CPR training business — pricing, scheduling, and beyond.",
    badge: "Premium",
  },
  {
    tag: "CORPORATE",
    name: "Group Training Sessions",
    price: "Custom Quote",
    duration: "Flexible",
    forWho: "Teams, hospitals, businesses",
    features: [
      "On-site or in-facility options",
      "Custom schedule & group size",
      "All courses available as group",
      "Volume pricing available",
    ],
    icon: "ri-group-line",
    featured: false,
    description: "We bring expert RN-led CPR training directly to your organization. Flexible scheduling, custom content, and volume pricing for groups of any size.",
  },
];

const SITE_URL = import.meta.env.VITE_SITE_URL || '';

export default function CoursesPage() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'CPR & Life Support Courses – Cardiocraft CPR Kissimmee',
    url: `${SITE_URL}/courses`,
    description: 'BLS, ACLS, PALS, Heartsaver CPR/AED and First Aid courses in Kissimmee, FL. RN-led, AHA-aligned, same-day certification.',
    itemListElement: courses.map((course, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Course',
        name: course.name,
        description: course.description,
        provider: {
          '@type': 'EducationalOrganization',
          name: 'Cardiocraft CPR',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Kissimmee',
            addressRegion: 'FL',
            addressCountry: 'US',
          },
        },
      },
    })),
  };

  return (
    <main className="pt-20">
      <title>CPR Courses Kissimmee | BLS ACLS PALS Certification | Cardiocraft CPR</title>
      <meta name="description" content="Browse all CPR certification courses in Kissimmee, FL. BLS, ACLS, PALS, Heartsaver CPR/AED, First Aid, and group training. RN instructors, AHA-aligned, same-day certification available." />
      <meta name="keywords" content="BLS certification Kissimmee, ACLS course Kissimmee, PALS certification Florida, Heartsaver CPR Kissimmee, CPR classes Kissimmee FL" />
      <link rel="canonical" href={`${SITE_URL}/courses`} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      {/* Hero */}
      <section className="relative py-24 overflow-hidden" style={{ background: "linear-gradient(135deg, #0d0d1a 0%, #1a0a2e 40%, #0f0f0f 100%)" }}>
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://public.readdy.ai/ai/img_res/19a9199566307418f6c286da26b1db2c.jpg"
            alt="Courses background"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-gold to-transparent opacity-50" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-gold to-transparent opacity-50" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <span
            className="inline-block text-brand-gold text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-6"
            style={{ background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.35)" }}
          >
            All Programs
          </span>
          <h1 className="font-serif text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            CPR &amp; Life Support Courses
            <br />
            <span
              style={{
                background: "linear-gradient(90deg, #D4AF37, #f5d97a, #D4AF37)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              in Kissimmee, FL
            </span>
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
            BLS, ACLS, PALS, Heartsaver, CPR/AED &amp; First Aid — all led by
            Registered Nurse instructors with same-day certification available.
          </p>
          <Link
            to="/booking#reserve-spot"
            className="inline-flex items-center gap-2 font-bold px-8 py-4 rounded-lg cursor-pointer whitespace-nowrap transition-all duration-300 hover:scale-105"
            style={{ background: "linear-gradient(135deg, #D4AF37, #b8952e)", color: "#1A1A1A", boxShadow: "0 4px 20px rgba(212,175,55,0.4)" }}
          >
            <i className="ri-calendar-check-line" />
            Book a Course Today
          </Link>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
            {courses.map((course) => (
              <div
                key={course.name}
                className="rounded-xl overflow-hidden border transition-all duration-300 hover:-translate-y-1 flex flex-col"
                style={
                  course.tag === "BEST VALUE"
                    ? { border: "2px solid #D4AF37", background: "linear-gradient(160deg, #faf8f0 0%, #fff 100%)" }
                    : course.featured
                    ? { border: "1.5px solid rgba(107,70,193,0.4)" }
                    : { border: "1px solid #f0f0f0" }
                }
              >
                {course.tag === "BEST VALUE" && (
                  <div className="bg-brand-gold text-brand-black text-xs font-black uppercase tracking-widest text-center py-2">
                    ⭐ Best Value — Most Popular Bundle
                  </div>
                )}
                {course.featured && course.tag !== "BEST VALUE" && (
                  <div className="bg-brand-purple text-white text-xs font-bold uppercase tracking-widest text-center py-2">
                    Most Popular
                  </div>
                )}
                <div className="p-7 flex flex-col flex-1">
                  <div className="flex items-start justify-between mb-5">
                    <div className="w-14 h-14 flex items-center justify-center bg-brand-purple-pale rounded-xl text-brand-purple shrink-0">
                      <i className={`${course.icon} text-2xl`} />
                    </div>
                    <div className="flex flex-col items-end gap-1.5">
                      <span className="text-xs font-bold uppercase tracking-wider bg-gray-100 text-gray-500 px-3 py-1 rounded-full whitespace-nowrap">
                        {course.tag}
                      </span>
                      {course.badge && course.tag !== "BEST VALUE" && (
                        <span
                          className="text-xs font-bold px-2.5 py-0.5 rounded-full whitespace-nowrap"
                          style={{ background: "rgba(107,70,193,0.12)", color: "#6B46C1", border: "1px solid rgba(107,70,193,0.2)" }}
                        >
                          {course.badge}
                        </span>
                      )}
                    </div>
                  </div>
                  <h2 className="font-serif font-bold text-xl text-brand-black mb-2 leading-snug">{course.name}</h2>
                  <p
                    className="font-serif font-bold text-3xl mb-1"
                    style={{ color: course.tag === "BEST VALUE" ? "#b8952e" : "#6B46C1" }}
                  >
                    {course.price}
                  </p>
                  <div className="flex gap-4 text-xs text-gray-400 mb-4">
                    <span><i className="ri-time-line mr-1" />{course.duration}</span>
                    <span><i className="ri-user-line mr-1" />{course.forWho}</span>
                  </div>
                  <p className="text-gray-500 text-sm leading-relaxed mb-5">{course.description}</p>
                  <ul className="space-y-2 mb-6 flex-1">
                    {course.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-gray-600">
                        <i className="ri-check-line text-brand-purple mt-0.5 flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/booking#reserve-spot"
                    className="block text-center font-semibold text-sm py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap mt-auto"
                    style={
                      course.tag === "BEST VALUE"
                        ? { background: "linear-gradient(135deg, #D4AF37, #b8952e)", color: "#1A1A1A" }
                        : course.featured
                        ? { background: "#6B46C1", color: "#fff" }
                        : { background: "#f5f3ff", color: "#6B46C1" }
                    }
                  >
                    Book This Course
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 border-t border-gray-100" style={{ background: "#fafafa" }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h3 className="font-serif text-3xl font-bold text-brand-black mb-4">
            Not Sure Which Course Is Right for You?
          </h3>
          <p className="text-gray-600 mb-8 leading-relaxed">
            Contact us and our RN instructors will help you find the perfect certification
            path for your role and goals.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="bg-brand-purple text-white font-semibold px-8 py-3.5 rounded-lg hover:bg-brand-purple-dark transition-colors cursor-pointer whitespace-nowrap"
            >
              Ask Us Anything
            </Link>
            <Link
              to="/booking#reserve-spot"
              className="font-bold px-8 py-3.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              style={{ background: "linear-gradient(135deg, #D4AF37, #b8952e)", color: "#1A1A1A" }}
            >
              Book Now
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
